import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, provideAppInitializer, inject } from '@angular/core';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { rootRouterConfig } from './app.routes';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FooterComponent } from './components/shared/footer/footer.component';
import { S3Component } from './components/shared/s3/s3.component';
import { LeftNavComponent } from './components/shared/left-nav/left-nav.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { StoreModule } from '@ngrx/store';
import { userPreferencesReducer } from './store/reducers/user-preferences-reducer';

import { AppService } from './app.service';
import { ErrorInterceptor } from './components/shared/interceptor/error.interceptor';
import { HeaderComponent } from './components/shared/header/header.component';
import { AgGridModule } from 'ag-grid-angular';
import { DataDropService } from './services/http.datadrop.service';
import { LeftMenuComponent } from './components/shared/left-menu/left-menu.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';  
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SharedModule } from './shared/shared.module';
import { StorageService } from './services/storage.service';
import { HttpRecipeStepMap } from './services/http.recipe.step-map.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { LoadingComponent } from './components/shared/loading/loading.component';

import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { S3Interceptor } from './core/interceptors/S3Interceptor';
import { CalendarModule } from 'primeng/calendar'
import { AuthConfigService } from './services/auth-config.service';

const appInitializerFn = (configService: AppService) => {
  return () => {
    console.log('[APP_INITIALIZER] appInitializerFn called');
    return configService.setConfig();
  };
};

// Factory to load config before app starts
export function loadAuthConfig(authConfigService: AuthConfigService) {
  return () => {
    console.log('[APP_INITIALIZER] loadAuthConfig called');
    return authConfigService.loadConfig().then(config => {
      console.log('[APP_INITIALIZER] loadAuthConfig completed, config:', config);
      return config;
    });
  };
}

// Factory to provide OktaAuth instance - returns a getter object
export function createOktaAuthFactory(authConfigService: AuthConfigService) {
  console.log('[OKTA_CONFIG] createOktaAuthFactory called');
  
  let oktaAuthInstance: OktaAuth | null = null;
  
  return {
    get oktaAuth() {
      console.log('[OKTA_CONFIG] oktaAuth getter accessed');
      if (!oktaAuthInstance) {
        oktaAuthInstance = authConfigService.createOktaAuth();
      }
      return oktaAuthInstance;
    }
  };
}

@NgModule({ 
    declarations: [
        AppComponent,
        FooterComponent,
        HeaderComponent,
        LeftNavComponent,
        S3Component,
        LeftMenuComponent,
        LoadingComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent], 
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        NgbModule,
        RouterModule.forRoot(rootRouterConfig, { useHash: false }),
        StoreModule.forRoot({ userPreferences: userPreferencesReducer }),
        AgGridModule,
        CalendarModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatInputModule,
        MatMenuModule,
        MatIconModule,
        SharedModule,
        NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
        OktaAuthModule
    ], 
    providers: [
        DataDropService,
        HttpRecipeStepMap,
        StorageService,
        AppService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
            const authConfigService = inject(AuthConfigService);
            return loadAuthConfig(authConfigService)();
        }),
        provideAppInitializer(() => {
            const appService = inject(AppService);
            return appInitializerFn(appService)();
        }),
        {
            provide: OKTA_CONFIG,
            deps: [AuthConfigService],
            useFactory: createOktaAuthFactory
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: S3Interceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi())
    ] 
})
export class AppModule { }